import { useCallback, useEffect, useMemo } from 'react';
import { useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';

import { DocumentDetailsPanel } from '~/components/Shared/DocumentDetailsPanel/DocumentDetailsPanel';
import { useAgent } from '~/contexts/AgentContext';
import { IconType } from '~/eds/src/types/IconType';
import { SharedPanelProps } from '~/eds/src/types/SharedPanelProps';
import { RedirectToXRayModal } from '~/features/x-ray/modals/RedirectToXRayModal';
import { FlagType } from '~/flags/flags';
import { useFlag } from '~/flags/useFlag';
import { useCurrentUser } from '~/hooks/useCurrentUser';
import { checkIsRbacAdmin } from '~/permissions/rbac/checkIsRbacAdmin';
import { slices } from '~/redux/slices';
import { useRouting } from '~/routing/useRouting';
import { Entity, EntityType } from '~/types/Entity';
import { Nullable } from '~/types/Nullable';
import { openInNewTab } from '~/utils/browser';
import { testIsAdmin } from '~/utils/user';

import { testPageHasSidebar } from '../ask-anything-button/utils';
import { ChatBot } from '../chatbot/ChatBot';
import { Citation, MessageAction } from '../types/chat';
import { EntityContext, EntityContextItem } from '../types/chatbot';
import { testIsFieldCitation } from '../utils/citations';
import { AGENT_IDS } from './agentIds';
import { agentRegistry } from './AgentRegistry';
import { COLLAPSED_WIDTH, RIGHT_DETAILS_PANEL_WIDTH } from './constants';
import { useWorkflowRoutingEnabled } from './hooks/useWorkflowRoutingEnabled';

const KNOWLEDGE_CENTER_URL =
  'https://support.evisort.com/hc/en-us/articles/1500006219501-Documents-Overview-Managing-Your-Documents';

interface ConversationHandler {
  onClick: () => void;
  isLoading: boolean;
}

interface PanelConfiguration {
  mode:
    | 'floating'
    | 'floating-full-width'
    | 'floating-sidepanel'
    | 'fullscreen'
    | 'inline';
  zIndex: number | string;
  isExpanded: boolean;
}

interface StatusMessage {
  message: string;
  status: 'danger' | 'warning' | 'info' | 'success' | 'active';
  title?: string;
  autoDismiss?: boolean;
}

export interface ChatBotPanelContentProps {
  panelConfig: PanelConfiguration;

  clearAction: ConversationHandler | null;
  setClearAction: React.Dispatch<
    React.SetStateAction<ConversationHandler | null>
  >;

  context: EntityContext;
  selectedContext: Nullable<EntityContextItem>;
  currentChatbot: Entity<EntityType>;
  activeCitation: Nullable<Citation>;
  docHandlerId: number | undefined;

  statusMessage: StatusMessage | undefined;
  disableInput: boolean;
  isEntityAndContextCompatible: boolean;
  isVisible: boolean;

  sideContent: SharedPanelProps['sideContent'];
  userMessageActions: (MessageAction & {
    resolveIcon?: (message: import('../types/chat').Message) => IconType;
  })[];
}

export const ChatBotPanelContent = ({
  panelConfig,
  clearAction,
  setClearAction,
  context,
  selectedContext,
  currentChatbot,
  activeCitation,
  docHandlerId,
  statusMessage,
  disableInput,
  isEntityAndContextCompatible,
  isVisible,
  sideContent,
  userMessageActions,
}: ChatBotPanelContentProps) => {
  const { location } = useRouting();
  const intl = useIntl();
  const dispatch = useDispatch();
  const currentUser = useCurrentUser();

  const hasQuestionGroups = useFlag(FlagType.AskAnythingQuestionGroups);
  const hasConversationalAIV2 = useFlag(FlagType.ConversationalAIV2);

  const isWorkflowRoutingEnabled = useWorkflowRoutingEnabled();
  const isAdministrator = useMemo(
    () => testIsAdmin(currentUser) || checkIsRbacAdmin(currentUser),
    [currentUser],
  );

  const agentContext = useAgent();
  const selectedAgent = agentContext?.selectedAgent ?? null;
  const selectAgent = agentContext?.selectAgent;

  const initialAgent = useSelector(slices.chatbot.selectors.selectInitialAgent);

  useEffect(() => {
    if (!isVisible && selectAgent) {
      selectAgent(null);
    }
  }, [isVisible, selectAgent]);

  useEffect(() => {
    if (isVisible && initialAgent && selectAgent && !selectedAgent) {
      selectAgent(initialAgent);
      dispatch(slices.chatbot.actions.setInitialAgent(null));
    }
  }, [isVisible, initialAgent, selectAgent, selectedAgent, dispatch]);

  const agentConfig = selectedAgent ? agentRegistry.get(selectedAgent) : null;
  const AgentComponent = agentConfig?.component;

  const panelTitle = useMemo(
    () =>
      intl.formatMessage({
        id: 'ChatBotPanel.title',
        defaultMessage: 'Ask AI',
        description: 'Title of the chatbot panel',
      }),
    [intl],
  );

  const lockIcon = useMemo(
    () => ({
      icon: 'lock' as const,
      tooltip: intl.formatMessage({
        id: 'ChatBotPanel.lockTooltip',
        defaultMessage:
          'This chat is only visible to you and system administrators.',
        description: 'Tooltip explaining chat privacy',
      }),
      position: 'right' as const,
    }),
    [intl],
  );

  const handleClosePanel = useCallback(() => {
    dispatch(slices.chatbot.actions.closeChatbot());
  }, [dispatch]);

  const handleTogglePanelMode = useCallback(() => {
    dispatch(
      slices.chatbot.actions.setPanelMode(
        panelConfig.isExpanded
          ? testPageHasSidebar(location.pathname)
            ? 'floating-sidepanel'
            : 'floating'
          : 'floating-full-width',
      ),
    );
  }, [dispatch, panelConfig.isExpanded, location.pathname]);

  const handleOpenKnowledgeCenter = useCallback(() => {
    openInNewTab(KNOWLEDGE_CENTER_URL);
  }, []);

  const handleHideCitation = useCallback(() => {
    dispatch(slices.chatbot.actions.setActiveCitation(null));
  }, [dispatch]);

  const expandCondenseAction = useMemo(
    () => ({
      text: panelConfig.isExpanded
        ? intl.formatMessage({
            id: 'ChatBotPanel.condenseAction',
            defaultMessage: 'Condense',
            description: 'Action button text to condense the panel',
          })
        : intl.formatMessage({
            id: 'ChatBotPanel.expandAction',
            defaultMessage: 'Expand',
            description: 'Action button text to expand the panel',
          }),
      onClick: handleTogglePanelMode,
      icon: (panelConfig.isExpanded ? 'minimize' : 'maximize') as
        | 'minimize'
        | 'maximize',
    }),
    [panelConfig.isExpanded, intl, handleTogglePanelMode],
  );

  const commonPanelProps = useMemo(
    () => ({
      title: panelTitle,
      mode: panelConfig.mode,
      width: COLLAPSED_WIDTH,
      moreActionsPosition: 'left' as const,
      icon: lockIcon,
      hidden: {
        isHidden: false,
        onHide: handleClosePanel,
      },
      zIndex: panelConfig.zIndex,
    }),
    [
      panelTitle,
      panelConfig.mode,
      panelConfig.zIndex,
      lockIcon,
      handleClosePanel,
    ],
  );

  const rightContent = useMemo(
    () =>
      activeCitation && testIsFieldCitation(activeCitation) && docHandlerId
        ? {
            content: (
              <DocumentDetailsPanel
                docHandlerId={docHandlerId}
                activeFieldId={activeCitation.entity?.id}
                isEmbedded
                enableFieldEditing={false}
                onHide={handleHideCitation}
                actionsConfig={{
                  rename: {
                    hidden: true,
                  },
                  delete: {
                    hidden: true,
                  },
                }}
              />
            ),
            isVisible: true,
            width: RIGHT_DETAILS_PANEL_WIDTH,
          }
        : undefined,
    [activeCitation, docHandlerId, handleHideCitation],
  );

  const chatBotActions = useMemo(
    () => [
      ...(clearAction
        ? [
            {
              icon: 'new-conversation' as const,
              tooltip: intl.formatMessage({
                id: 'ChatBot.newConversationTooltip',
                defaultMessage: 'New Conversation',
                description: 'Tooltip for new conversation button',
              }),
              onClick: clearAction.onClick,
              disabled: clearAction.isLoading,
              text: '',
            },
          ]
        : []),
      {
        icon: 'help' as const,
        tooltip: intl.formatMessage({
          id: 'ChatBotPanel.knowledgeCenterTooltip',
          defaultMessage: 'Knowledge Center',
          description: 'Tooltip for knowledge center help button',
        }),
        onClick: handleOpenKnowledgeCenter,
        text: '',
      },
      expandCondenseAction,
    ],
    [clearAction, intl, expandCondenseAction, handleOpenKnowledgeCenter],
  );

  const chatBotPanelProps = useMemo(
    () => ({
      ...commonPanelProps,
      mode: panelConfig.mode as
        | 'floating'
        | 'floating-full-width'
        | 'floating-sidepanel'
        | 'fullscreen'
        | 'inline',
      actions: chatBotActions,
      sideContent: hasQuestionGroups ? sideContent : undefined,
      rightContent,
      zIndex: String(panelConfig.zIndex),
    }),
    [
      commonPanelProps,
      chatBotActions,
      hasQuestionGroups,
      sideContent,
      rightContent,
      panelConfig.zIndex,
    ],
  );

  const shouldShowWorkflowRoutingAgent =
    selectedAgent === AGENT_IDS.WORKFLOW_ROUTING &&
    AgentComponent &&
    selectAgent &&
    (isWorkflowRoutingEnabled || isAdministrator);

  if (shouldShowWorkflowRoutingAgent) {
    return (
      <>
        <AgentComponent
          onResetAgent={() => selectAgent(null)}
          panelProps={{
            ...commonPanelProps,
            actions: [expandCondenseAction],
          }}
        />
        <RedirectToXRayModal />
      </>
    );
  }

  return (
    <>
      <ChatBot
        panelProps={chatBotPanelProps}
        hasAskAnything={true}
        context={context}
        selectedContext={selectedContext}
        disableInput={!!disableInput || !isEntityAndContextCompatible}
        statusMessage={statusMessage}
        entity={currentChatbot}
        userMessageActions={hasQuestionGroups ? userMessageActions : []}
        onSelectContextFilter={(context) => {
          if (hasConversationalAIV2) {
            dispatch(slices.chatbot.actions.setSelectedContext(context));
          } else {
            dispatch(slices.chatbot.actions.setSelectedContextInterim(context));
          }
        }}
        onSelectMessageSource={(message, sourceIndex) => {
          if (message.sources && message.sources.length) {
            dispatch(slices.chatbot.actions.setSources(message.sources));
            dispatch(slices.chatbot.actions.setActiveSourceIndex(sourceIndex));
          }
        }}
        onRegisterClearAction={setClearAction}
      />
      <RedirectToXRayModal />
    </>
  );
};
